/**
 * Playback-sync bootstrap
 *
 * Installs the API-backed PlayedChaptersProvider after config + auth are
 * ready. The factory is held in module scope so `signOutCleanup()` can be
 * invoked from `src/utils/auth.js`'s `logout()` flow without going through
 * a React render.
 *
 * Bootstrapping is idempotent — repeated invocations are no-ops.
 */

import { getApiUrl } from '../config';
import {
  getAccessToken,
  decodeToken,
  logout,
  registerSignOutCleanup,
} from './auth';
import { setPlayedChaptersProvider } from './playedChapters';
import { createPlaybackCoalescer } from './playbackCoalescer';
import { createPlaybackSyncQueue } from './playbackSyncQueue';
import { createApiPlayedChaptersProvider } from './apiPlayedChapters';

const COALESCE_WINDOW_MS = 10000;
const QUEUE_KEY_PREFIX = 'playback-sync-queue-v1';

let activeSub = null;
let activeApiProvider = null;
let activeQueue = null;
let activeCoalescer = null;

function getCurrentUserSub() {
  const token = getAccessToken();
  if (!token) return null;
  const decoded = decodeToken(token);
  return decoded && typeof decoded.sub === 'string' ? decoded.sub : null;
}

/**
 * Install the API-backed PlayedChaptersProvider for the currently signed-in
 * user. Idempotent per `sub` — a second call from the same account is a
 * no-op. When the active sub changes (account switch) the provider is rebuilt
 * with a sub-namespaced queue key so cross-account state cannot leak (T033).
 * When no user is signed in this is a no-op; the legacy localStorage provider
 * stays installed.
 */
export function bootstrapPlaybackSync() {
  const sub = getCurrentUserSub();
  if (!sub) return;
  if (activeSub === sub && activeApiProvider) return;
  try {
    activeSub = sub;
    activeCoalescer = createPlaybackCoalescer({ windowMs: COALESCE_WINDOW_MS });
    activeQueue = createPlaybackSyncQueue({ storageKey: `${QUEUE_KEY_PREFIX}::${sub}` });
    activeQueue.hydrate();
    activeApiProvider = createApiPlayedChaptersProvider({
      getApiUrl,
      getAccessToken,
      onUnauthorized: logout,
      getCurrentUserSub,
      coalescer: activeCoalescer,
      queue: activeQueue,
    });
    setPlayedChaptersProvider(activeApiProvider);
    // Wire signOutCleanup into auth.logout (FR-007 / SC-003).
    registerSignOutCleanup(() => activeApiProvider && activeApiProvider.signOutCleanup());
  } catch (e) {
    console.warn('PlaybackSyncBootstrap failed:', e);
  }
}

export function getPlaybackSyncProvider() {
  return activeApiProvider;
}

export function getPlaybackSyncQueueInstance() {
  return activeQueue;
}

export function getPlaybackSyncCoalescer() {
  return activeCoalescer;
}

// Test-only: allow Chapters.test.js to swap in a coalescer with spies so the
// pause / close flush wiring (FR-014a / SC-014) can be observed without the
// full bootstrapPlaybackSync() flow running. Production code MUST NOT call
// this; passing `null` resets the slot.
export function _setPlaybackSyncCoalescerForTest(coalescer) {
  activeCoalescer = coalescer;
}
